import { boot } from 'quasar/wrappers'
import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from 'axios'
import { StateInterface } from 'src/store'
import { AccessToken, RawAccessToken } from 'src/apis/auth'
import { appTheme } from 'src/system/app-config'

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance
  }
}

interface RequestHeaders {
  [key: string]: string
}

interface AxiosCustomConfig extends AxiosRequestConfig {
  _retry: boolean
}
interface AxiosCustomError<T = unknown> extends AxiosError<T> {
  config: AxiosCustomConfig
}

const api = axios.create()


export default boot(({ app, store }) => {
  app.config.globalProperties.$axios = axios
  app.config.globalProperties.$api = api

  api.interceptors.request.use(
    (config) => {
      const state = store.state as StateInterface
      const url = `${state.config.endpoint || ''}/${state.config.slug}/`
      config.url = `${url}${config.url || ''}`

      if (state.auth.accessToken) {
        const headers = config.headers as RequestHeaders
        if (!headers.Authorization) {
          headers.Authorization = `Bearer ${state.auth.accessToken}`
        }
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  api.interceptors.response.use(
    (response) => {
      return response
    },
    async (error: AxiosCustomError) => {
      const originalRequest = error.config
      if (
        error.response &&
        error.response.status === 403 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true

        const state = store.state as StateInterface
        try {
          const result: AxiosResponse<RawAccessToken> = await api.post(
            'private/v1/auth/refresh/',
            {
              refresh_token: state.auth.refreshToken,
            }
          )

          // Update store
          store.dispatch('auth/updateToken', <AccessToken>{
            accessToken: result.data.access_token,
            accessTokenExpiredDate: result.data.access_token_expired_date,
            refreshToken: result.data.refresh_token,
            refreshTokenExpiredDate: result.data.refresh_token_expired_date,
          })

          const headers = originalRequest.headers as RequestHeaders
          headers.Authorization = `Bearer ${result.data.access_token}`

          const temp = originalRequest.url?.split(`${state.config.slug}/`)
          if (temp) {
            originalRequest.url = temp[1]
          }

          return api(originalRequest)
        } catch (e) {
			if(e) {
				localStorage.clear()
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const slug =  appTheme.getConfig().slug
        window.location.href = `/${slug}/login`
        
			}
		}
      }
      return Promise.reject(error)
    }
  )
})

export { axios, api }
